<template>
  <div class="particle-container">
    <canvas id="canvas-3d" ref="canvasRef" />
  </div>
</template>

<script setup>
  import { ref, onMounted, onUnmounted, inject } from 'vue'

  const canvasRef = ref(null)
  const theme = inject('theme')
  let animationId = null
  let resizeHandler = null
  let scrollHandler = null
  let particles = []
  let tSY = 0
  let cS = 0
  let sY = 0

  class StarParticle {
    constructor(i, total) {
      this.baseSize = Math.random() * 2 + 2
      const r = Math.random()
      this.color =
        r < 0.85
          ? { h: 260 + Math.random() * 20, s: 85 + Math.random() * 15, l: 55 + Math.random() * 10 }
          : r < 0.93
            ? {
                h: 320 + Math.random() * 20,
                s: 85 + Math.random() * 15,
                l: 60 + Math.random() * 10
              }
            : {
                h: 180 + Math.random() * 20,
                s: 85 + Math.random() * 15,
                l: 55 + Math.random() * 10
              }
      this.alpha = 0.95
      this.angleX = Math.random() * Math.PI * 2
      this.angleY = Math.random() * Math.PI * 2
      this.rotSpeedX = (Math.random() - 0.5) * 0.0025
      this.rotSpeedY = (Math.random() - 0.5) * 0.0025
      const phi = Math.acos(1 - (2 * (i + 0.5)) / total)
      const theta = Math.PI * (1 + Math.sqrt(5)) * (i + 0.5)
      const rad = 450 + Math.random() * 100
      this.originX = rad * Math.sin(phi) * Math.cos(theta)
      this.originY = rad * Math.sin(phi) * Math.sin(theta)
      this.originZ = rad * Math.cos(phi)
      this.x = this.originX
      this.y = this.originY
      this.z = this.originZ
    }

    update(scrollY) {
      this.angleX += this.rotSpeedX
      this.angleY += this.rotSpeedY
      const cX = Math.cos(this.angleX)
      const sXVal = Math.sin(this.angleX)
      const cY = Math.cos(this.angleY)
      const sYVal = Math.sin(this.angleY)
      let x1 = this.originX * cY - this.originZ * sYVal
      let z1 = this.originX * sYVal + this.originZ * cY
      let y1 = this.originY * cX - z1 * sXVal
      let z2 = this.originY * sXVal + z1 * cX
      const exp = Math.min(scrollY / 1200, 1)
      const rad = 450 + exp * 1200
      const scale = rad / 450
      this.x = x1 * scale
      this.y = y1 * scale
      this.z = z2 * scale
    }

    draw(ctx, canvas, scrollY) {
      const fov = 600
      const camZ = 400
      const scale = fov / (fov + this.z + camZ)
      const sx = canvas.width / 2 + this.x * scale
      const sy = canvas.height / 2 + this.y * scale
      const sz = Math.max(1.5, this.baseSize * scale * 2)
      if (sx < -100 || sx > canvas.width + 100 || sy < -100 || sy > canvas.height + 100) return
      const scrollFactor = Math.min(1, scrollY / 400)
      const dF = Math.max(0.7, Math.min(1, (this.z + 800) / 1200))
      const baseAlpha = 0.35 + scrollFactor * 0.6
      const a = dF * baseAlpha
      const haloFactor = 1.5 + scrollFactor * 1
      const oR = Math.max(2, sz * haloFactor)
      const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, oR)
      g.addColorStop(0, `hsla(0,0%,100%,${a})`)
      g.addColorStop(0.3, `hsla(${this.color.h},${this.color.s}%,${this.color.l + 20}%,${a * 0.8})`)
      g.addColorStop(1, `hsla(${this.color.h},${this.color.s}%,${this.color.l}%,0)`)
      ctx.beginPath()
      ctx.arc(sx, sy, oR, 0, Math.PI * 2)
      ctx.fillStyle = g
      ctx.fill()
      ctx.beginPath()
      const cR = Math.max(0.8, sz * 0.6)
      ctx.arc(sx, sy, cR, 0, Math.PI * 2)
      ctx.fillStyle = `hsla(0,0%,100%,${a * 0.9})`
      ctx.fill()
    }
  }

  function drawBackground(ctx, canvas, currentTheme) {
    // 绘制渐变背景
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height)
    if (currentTheme === 'dark') {
      gradient.addColorStop(0, '#0f172a')
      gradient.addColorStop(0.4, '#1e293b')
      gradient.addColorStop(1, '#334155')
    } else {
      gradient.addColorStop(0, '#f8fafc')
      gradient.addColorStop(0.5, '#e2e8f0')
      gradient.addColorStop(1, '#cbd5e1')
    }
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }

  function animate(canvas, ctx, windowScrollY, currentTheme) {
    cS += (tSY - cS) * 0.1
    sY = cS

    const scrollY = windowScrollY()

    // 绘制背景
    drawBackground(ctx, canvas, currentTheme.value)

    // 更新和绘制粒子
    particles.sort((a, b) => b.z - a.z)
    particles.forEach((p) => {
      p.update(sY)
      p.draw(ctx, canvas, scrollY)
    })

    // 连线
    const isS = Math.abs(tSY - cS) > 0.5
    const mLD = sY < 600 ? 150 : 220
    const scrollFactor = Math.min(1, scrollY / 400)
    const lA = (sY < 600 ? 0.1 : 0.18) * (0.3 + scrollFactor * 0.7)
    ctx.lineWidth = 1.2
    let lC = 0
    const mL = isS ? 200 : 350
    const st = isS ? 4 : 3

    for (let i = 0; i < particles.length; i += st) {
      if (lC >= mL) break
      for (let j = i + 1; j < particles.length; j += st) {
        if (lC >= mL) break
        const p1 = particles[i]
        const p2 = particles[j]
        const dx = p1.originX - p2.originX
        const dy = p1.originY - p2.originY
        const dz = p1.originZ - p2.originZ
        const oD = Math.sqrt(dx * dx + dy * dy + dz * dz)
        const exp = Math.min(sY / 1200, 1)
        const cMD = mLD * (1 + exp * 1.3)
        if (oD < cMD) {
          const fov = 600
          const camZ = 400
          const s1 = fov / (fov + p1.z + camZ)
          const s2 = fov / (fov + p2.z + camZ)
          const x1 = canvas.width / 2 + p1.x * s1
          const y1 = canvas.height / 2 + p1.y * s1
          const x2 = canvas.width / 2 + p2.x * s2
          const y2 = canvas.height / 2 + p2.y * s2
          const dA = 1 - oD / cMD
          const a = dA * lA
          ctx.strokeStyle = `hsla(260,75%,70%,${a})`
          ctx.beginPath()
          ctx.moveTo(x1, y1)
          ctx.lineTo(x2, y2)
          ctx.stroke()
          lC++
        }
      }
    }

    animationId = requestAnimationFrame(() => animate(canvas, ctx, windowScrollY, currentTheme))
  }

  onMounted(() => {
    const canvas = canvasRef.value
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // 设置 canvas 尺寸
    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    resizeHandler = resize
    window.addEventListener('resize', resize)

    // 创建粒子
    const particleCount = 200
    particles = []
    for (let i = 0; i < particleCount; i++) {
      particles.push(new StarParticle(i, particleCount))
    }

    // 滚动监听
    scrollHandler = () => {
      tSY = window.scrollY
    }
    window.addEventListener('scroll', scrollHandler, { passive: true })

    // 获取滚动位置的函数
    const getWindowScrollY = () => window.scrollY

    // 开始动画
    animate(canvas, ctx, getWindowScrollY, theme)
  })

  onUnmounted(() => {
    if (animationId) {
      cancelAnimationFrame(animationId)
      animationId = null
    }
    if (resizeHandler) {
      window.removeEventListener('resize', resizeHandler)
    }
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler)
    }
    particles = []
  })
</script>

<style scoped>
  .particle-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    pointer-events: none;
  }

  #canvas-3d {
    width: 100%;
    height: 100%;
  }
</style>
